/**
 * Utility to upload a file to a presigned URL
 */
export async function uploadFileToUrl(url: string, file: File): Promise<void> {
  const response = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
}

/**
 * Convert a base64 string to a File object
 * @param base64 - Base64 encoded string (with or without data URL prefix)
 * @param fileName - Name for the resulting file
 * @param mimeType - MIME type for the file
 * @throws {Error} If input validation fails or base64 decoding fails
 */
export function base64ToFile(base64: string, fileName: string, mimeType: string): File {
  // 1. Validate fileName and mimeType are non-empty strings
  if (!fileName || typeof fileName !== "string" || fileName.trim() === "") {
    throw new Error("Invalid fileName: must be a non-empty string");
  }
  if (!mimeType || typeof mimeType !== "string" || mimeType.trim() === "") {
    throw new Error("Invalid mimeType: must be a non-empty string");
  }

  // 2. Validate base64 is a non-empty string
  if (!base64 || typeof base64 !== "string" || base64.trim() === "") {
    throw new Error("Invalid base64: must be a non-empty string");
  }

  // 3. Strip data URL prefix if present (e.g., "data:image/jpeg;base64,")
  let cleanBase64 = base64.trim();
  const dataUrlMatch = cleanBase64.match(/^data:[^;]+;base64,(.+)$/);
  if (dataUrlMatch) {
    cleanBase64 = dataUrlMatch[1];
  }

  // 4. Validate base64 format using a safe regex
  // Base64 characters: A-Z, a-z, 0-9, +, /, = (padding)
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Regex.test(cleanBase64)) {
    throw new Error("Invalid base64: contains invalid characters or malformed padding");
  }

  // 5. Attempt to decode base64 with error handling
  let byteString: string;
  try {
    byteString = atob(cleanBase64);
  } catch (error) {
    throw new Error(`Failed to decode base64: ${error instanceof Error ? error.message : "unknown error"}`);
  }

  // 6. Convert to ArrayBuffer and File
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  const blob = new Blob([ab], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
}

/**
 * Convert a File object to base64 string
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const parts = result.split(",");
      if (parts.length < 2) {
        reject(new Error("Invalid data URL format: missing base64 data after comma"));
        return;
      }
      const base64 = parts[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
}
